import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../../services/notifications.service';
import { IError } from '../../../../../interfaces';
import { CHANNEL_TYPE, DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { DialogService } from '../../../../../services/dialog.service';
import { IntegrationTelegramBotService } from '../../../../../services/integration-telegram-bot.service';
import { ChannelService } from '../../../../../services/channel.service';

interface ITelegramBotControlForm {
  token: FormControl;
}

@Component({
  selector: 'app-integration-telegram-bot',
  templateUrl: './integration-telegram-bot.component.html',
  styleUrls: ['./integration-telegram-bot.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationTelegramBotComponent implements OnInit, OnDestroy {
  public form!: FormGroup<ITelegramBotControlForm>;
  private readonly destroy$ = new Subject<null>();
  public isFormSubmitted: boolean = false;
  public channel: any;
  private channel_id!: string;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    private readonly integrationTelegramBotService: IntegrationTelegramBotService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
    private readonly channelService: ChannelService,
  ) {}

  ngOnInit() {
    this.channel_id = this.payload.channel.id;
    this.getChannel();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  initForm() {
    if (this.channel) {
      this.form = new FormGroup<ITelegramBotControlForm>({
        token: new FormControl(this.channel.x_data?.token || '', Validators.required),
      });
    } else {
      this.form = new FormGroup<ITelegramBotControlForm>({
        token: new FormControl('', Validators.required),
      });
    }
  }

  onSubmit() {
    this.channelService.checkConnect({ type: CHANNEL_TYPE.TELEGRAM_BOT }).subscribe({
      next: (data: any) => {
        if (data) {
          this.isFormSubmitted = true;

          if (this.form.valid) {
            const payload = this.form.getRawValue();

            if (!this.channel) {
              this.integrationTelegramBotService
                .addTelegramBotApi({ ...payload, channel_id: this.channel_id })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: () => {
                    this.notificationService.showSuccess(
                      '',
                      activeTranslations.notifications_texts.success
                        .successfully_updated,
                    );
                    this.close.emit('success');
                  },
                  error: (e) => {
                    (e.error?.errors || []).forEach((err: IError) => {
                      this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                    });
                  },
                });
            } else {
              this.integrationTelegramBotService
                .updateTelegramBotApi(payload, this.channel_id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: () => {
                    this.notificationService.showSuccess(
                      '',
                      activeTranslations.notifications_texts.success
                        .successfully_added,
                    );
                    this.close.emit('success');
                  },
                  error: (e) => {
                    // this.notificationService.showError('Помилка', 'Даний API ключ вже підключений', 5000)
                    (e.error?.errors || []).forEach((err: IError) => {
                      this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                    });
                  },
                });
            }
          }
        }
      },
    });
  }

  deleteApi() {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.integration-telegram-bot',
    });

    dialogRef.subscribe({
      next: (res) => {
        if (res) {
          this.integrationTelegramBotService
            .deleteToken(this.channel.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.notificationService.showSuccess(
                  '',
                  activeTranslations.notifications_texts.success.successfully_deleted,
                );
                this.close.emit('success');
              },
              error: (e) => {
                (e.error?.errors || []).forEach((err: IError) => {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                });
              },
            });
        }
      },
      error: (e) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });
      },
    });
  }

  getChannel() {
    this.channelService
      .getChannelById(this.channel_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.channel = v;
        this.initForm();
      });
  }
}
